import React from 'react';
import { useTranslation } from 'react-i18next';
import { ROT_CUSTOMER_LIABILITY_TEXT, ROT_INFORMATION_TEXT } from '../../constants/contractTerms';
import type { ContractType, DocumentRecord } from '../../types';
import { ContractAttachmentList } from '../ContractAttachmentList';
import type { CompanyTheme } from '../company-theme/companyTheme';
import { useThemeLogo } from '../company-theme/useThemeLogo';
import { formatKronor } from './milestonePlan';
import { isRichTextEmpty } from './useContractWizardDraft';

export interface PreviewMilestone {
  number: number;
  description: string;
  /** Amount after ROT (what the customer pays for the stage). */
  afterRotAmount: number;
  endDate?: string;
  /** An ÄTA price adjustment instead of a stage in the plan (no own delivery). */
  kind?: 'milestone' | 'price_adjustment';
}

/** Another contract the previewed one refers to (ÄTA parent, replaced version). */
export interface PreviewContractRef {
  contractNumber: number;
  contractVersion?: number;
  /** Agreement name, '' while the contract has none (869fnhht3). */
  contractName: string;
  contractType?: ContractType;
  variationNumber?: number | null;
}

export interface ContractDocumentPreviewProps {
  contractNumber: number;
  contractVersion?: number;
  contractName: string;
  companyName: string;
  organizationNumber?: string;
  customerName?: string;
  customerEmail?: string;
  assignmentHtml: string;
  referToAttachments: boolean;
  otherHtml: string;
  gross: number;
  rot: number;
  afterRot: number;
  milestones: PreviewMilestone[];
  attachments: DocumentRecord[];
  /** "Ändra" per section; omit to render read-only. */
  onEditAssignment?: () => void;
  onEditPricing?: () => void;
  /** Label on the paper's corner chip; default "Förhandsvisning". */
  chipLabel?: string;
  /**
   * Signing has started: the parties' signing dates (ISO) instead of today's
   * date; a party without a date is shown as awaiting its signature.
   */
  signedAt?: { company?: string | null; customer?: string | null };
  /** Default fixed price. */
  contractType?: ContractType;
  variationNumber?: number | null;
  /** ÄTA: the agreement it adds to. */
  parentContract?: PreviewContractRef | null;
  /** Replacement contract: the version it replaces. */
  replaces?: PreviewContractRef | null;
  /** Scoped ÄTA: the main agreement that remains in force with its amendments. */
  amendmentContract?: PreviewContractRef | null;
  previousVariationNumber?: number | null;
  /** Shown above the title block (e.g. the conflict ÄTA notice). */
  notice?: React.ReactNode;
  /** Replaces amounts and payment schedule (e.g. the read-only stage variation). */
  pricingOverride?: React.ReactNode;
  /** Heading of the payment schedule: the plan, or an ÄTA price change. */
  scheduleKind?: 'plan' | 'price_change' | 'change';
  /** i18n key of the payment terms line; default contracts.preview.paymentTerms. */
  paymentTermsKey?: string;
  /** With both, attachments can be opened and verified (review and signing). */
  onOpenAttachment?: (doc: DocumentRecord) => void;
  onVerifyAttachment?: (doc: DocumentRecord) => void;
  /**
   * The contracting company's theme (logo and colours); Etappio's standard
   * and the company-name mark while it is loading or missing.
   */
  theme?: CompanyTheme | null;
}

const contractTypeText = (
  t: (key: string, options?: Record<string, unknown>) => string,
  type: ContractType | undefined,
  variationNumber: number | null | undefined,
) => {
  if (type !== 'variation') return t('contracts.types.fixed');
  return variationNumber != null
    ? `${t('contracts.types.variation')} (${t('projectView.ata.short')} #${variationNumber})`
    : t('contracts.types.variation');
};

/**
 * The contract as the customer will see it, built from the wizard's data. Same
 * sections and wording as the contract page's preview (and the signed PDF):
 * Avtal · Parter · Uppdragsbeskrivning · Övriga villkor · Pris och
 * betalningsvillkor · Särskilda villkor samt bilagor · Underskrifter.
 * The header shows the company's logo from its theme, otherwise the company
 * name; Etappio's logo is never shown in the contract.
 */
export function ContractDocumentPreview(props: ContractDocumentPreviewProps) {
  const { t } = useTranslation();
  const {
    contractNumber, contractVersion, contractName, companyName, organizationNumber,
    customerName, customerEmail, assignmentHtml, referToAttachments, otherHtml,
    gross, rot, afterRot, milestones, attachments, onEditAssignment, onEditPricing, chipLabel, signedAt,
    contractType, variationNumber, parentContract, replaces, amendmentContract, previousVariationNumber, notice, pricingOverride, scheduleKind = 'plan',
    paymentTermsKey = 'contracts.preview.paymentTerms', onOpenAttachment, onVerifyAttachment, theme,
  } = props;
  const { src: logoSrc } = useThemeLogo(theme?.logoUrl);
  // No Beställare yet (ClickUp 869fmxjgt): a marked placeholder that can never
  // be mistaken for real details. Only the preview shows it; nothing is sent
  // or signed without a Beställare.
  const customerPlaceholder = (
    <p className="mb-0 contract-preview-placeholder" data-testid="contract-preview-customer-placeholder">
      <span>{t('contracts.preview.customerPlaceholder')}</span>
      <span className="project-customer-pill is-missing ms-2">{t('projectCustomer.status.missing')}</span>
    </p>
  );
  // The theme comes from data, so its colours are CSS variables (styling.md §6).
  const themeStyle = theme
    ? ({
        '--contract-preview-heading': theme.headingColor,
        '--contract-preview-text': theme.textColor,
        '--contract-preview-accent': theme.accentColor,
      } as React.CSSProperties)
    : undefined;
  const isVariation = contractType === 'variation';
  const kr = t('contracts.wizard.pricing.unitKr');
  const today = new Date().toLocaleDateString('sv-SE');
  const signatureLine = (value: string | null | undefined) =>
    !signedAt
      ? `${t('contracts.preview.date')}: ${today}`
      : value
        ? `${t('contracts.preview.date')}: ${new Date(value).toLocaleDateString('sv-SE')}`
        : t('contracts.preview.awaitingSignature');
  const hasRot = rot !== 0;

  const editButton = (onClick: (() => void) | undefined, label: string) =>
    onClick ? (
      <button type="button" className="btn btn-link contract-wizard-edit-link" onClick={onClick} aria-label={label}>
        {t('contracts.wizard.review.edit')}
      </button>
    ) : null;

  const sectionHeading = (title: string, edit?: React.ReactNode) => (
    <div className="d-flex justify-content-between align-items-center gap-2">
      <h3 className="contract-preview-heading">{title}</h3>
      {edit}
    </div>
  );

  return (
    <article className="contract-preview-paper" aria-label={t('contracts.wizard.review.previewAria')} style={themeStyle}>
      <header className="contract-preview-header">
        {theme?.logoUrl && logoSrc ? (
          <img src={logoSrc} alt={t('settings.theme.logo.alt', { company: companyName })} className="contract-preview-logo" />
        ) : (
          <span className="contract-preview-company">{companyName || '—'}</span>
        )}
        <span className="contract-preview-chip">{chipLabel ?? t('contracts.wizard.review.previewChip')}</span>
      </header>

      {notice}

      <div className="contract-preview-title-block">
        <p className="contract-wizard-kicker mb-1">
          {amendmentContract
            ? t('variationWizard.amendmentContract', { number: variationNumber, contract: amendmentContract.contractNumber })
            : !isVariation
            ? t('contracts.wizard.review.contractKicker', { number: contractNumber, version: contractVersion ?? 1 })
            : variationNumber != null
              ? t('contracts.wizard.review.contractKickerVariationNumber', { number: contractNumber, version: contractVersion ?? 1, variation: variationNumber })
              : t('contracts.wizard.review.contractKickerVariation', { number: contractNumber, version: contractVersion ?? 1 })}
        </p>
        <p className="contract-preview-name">{contractName || '—'}</p>
        {amendmentContract && <div className="small mt-2" data-testid="amendment-contract-reference">
          <p className="mb-0">{t('variationWizard.amendsContract', { number: amendmentContract.contractNumber })}</p>
          {previousVariationNumber != null && <p className="text-muted mb-0">{t('variationWizard.previousBasis', { number: previousVariationNumber, contract: amendmentContract.contractNumber })}</p>}
        </div>}
        {replaces && (
          <p className="small mb-0 mt-2" data-testid="replacement-contract-reference">
            <strong>{t('projectView.replacement.replaces')}</strong>{' '}
            {t('projectView.replacement.contractRef', {
              number: replaces.contractNumber,
              version: replaces.contractVersion ?? 1,
              name: replaces.contractName || '—',
            })}
          </p>
        )}
      </div>

      <section className="contract-preview-section">
        {sectionHeading(t('contracts.preview.partiesTitle'))}
        <p className="text-muted small">{t('contracts.preview.partiesIntro')}</p>
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <div className="contract-preview-party">
              <p className="contract-preview-party-role">{t('contracts.preview.company')}</p>
              <p className="mb-0 fw-semibold">{companyName || '—'}</p>
              <p className="mb-0 small text-muted">{t('contracts.preview.orgNumber')}: {organizationNumber || '—'}</p>
            </div>
          </div>
          <div className="col-12 col-md-6">
            <div className="contract-preview-party">
              <p className="contract-preview-party-role">{t('contracts.preview.orderer')}</p>
              {customerName ? (
                <>
                  <p className="mb-0 fw-semibold">{customerName}</p>
                  <p className="mb-0 small text-muted">{t('contracts.preview.customerEmail')}: {customerEmail || '—'}</p>
                </>
              ) : customerPlaceholder}
            </div>
          </div>
        </div>
      </section>

      <section className="contract-preview-section">
        {sectionHeading(t('contracts.descriptions.title'), editButton(onEditAssignment, t('contracts.wizard.review.editAssignmentAria')))}
        {!isRichTextEmpty(assignmentHtml) && (
          // Same trusted-editor HTML the contract page's preview renders.
          <div className="contract-preview-richtext" dangerouslySetInnerHTML={{ __html: assignmentHtml }} />
        )}
        {referToAttachments && <p className="mb-0">{t('contracts.descriptions.referToAttachmentsText')}</p>}
        {isRichTextEmpty(assignmentHtml) && !referToAttachments && <p className="text-muted mb-0">—</p>}
      </section>

      <section className="contract-preview-section">
        {sectionHeading(t('contracts.descriptions.otherTitle'), editButton(onEditAssignment, t('contracts.wizard.review.editOtherAria')))}
        {isRichTextEmpty(otherHtml)
          ? <p className="text-muted mb-0">—</p>
          : <div className="contract-preview-richtext" dangerouslySetInnerHTML={{ __html: otherHtml }} />}
      </section>

      <section className="contract-preview-section">
        {sectionHeading(t('contracts.preview.priceTitle'), editButton(onEditPricing, t('contracts.wizard.review.editPricingAria')))}
        <dl className="contract-preview-amounts">
          <div>
            <dt>{t('contracts.preview.contractTypeLabel')}</dt>
            <dd>{contractTypeText(t, contractType, variationNumber)}</dd>
          </div>
          {isVariation && parentContract && (
            <div>
              <dt>{t('contracts.preview.parentContractLabel')}</dt>
              <dd className="text-break">
                #{parentContract.contractNumber} — {parentContract.contractName || '—'} ({contractTypeText(t, parentContract.contractType, parentContract.variationNumber)})
              </dd>
            </div>
          )}
          {!pricingOverride && (
            <div>
              <dt>{t('contracts.preview.agreedAmount')}</dt>
              <dd>{formatKronor(gross)} {kr} {t('contracts.preview.inclVat')}</dd>
            </div>
          )}
          {!pricingOverride && hasRot && (
            <>
              <div>
                <dt>{t('projectView.rot.planned')}</dt>
                <dd>−{formatKronor(rot)} {kr}</dd>
              </div>
              <div className="is-total">
                <dt>{t('projectView.rot.afterRot')}</dt>
                <dd>{formatKronor(afterRot)} {kr}</dd>
              </div>
            </>
          )}
        </dl>
        {pricingOverride}
        {!pricingOverride && hasRot && (
          <div className="contract-preview-note">
            <p className="mb-2">{ROT_INFORMATION_TEXT}</p>
            <p className="mb-0" data-testid="contract-rot-liability-terms">{ROT_CUSTOMER_LIABILITY_TEXT}</p>
          </div>
        )}
        {!pricingOverride && milestones.length > 0 && (
          <>
            <p className="contract-preview-label fw-semibold mt-3 mb-2">
              {scheduleKind === 'change'
                ? t('projectView.ata.change')
                : scheduleKind === 'price_change'
                  ? t('projectView.ata.priceChange')
                  : t('contracts.preview.paymentSchedule')}
            </p>
            <ol className="contract-preview-milestones">
              {milestones.map((milestone, index) => {
                const adjustment = milestone.kind === 'price_adjustment';
                return (
                <li key={`${milestone.kind ?? 'milestone'}-${milestone.number}`}>
                  <span className="contract-preview-milestone-number" aria-hidden="true">{adjustment ? '±' : milestone.number}</span>
                  <span className="contract-preview-milestone-body">
                    <span className="d-block fw-semibold text-break">
                      {adjustment
                        ? milestone.afterRotAmount === 0 ? t('projectView.ata.change') : t('projectView.ata.priceAdjustment')
                        : milestone.description || '—'}
                    </span>
                    <span className="d-block small text-muted">
                      {adjustment
                        ? milestone.afterRotAmount === 0 ? t('projectView.ata.costNeutral') : t('projectView.ata.noNewPlanMilestone')
                        : index < milestones.length - 1
                          ? t('contracts.preview.paymentStage', { number: milestone.number })
                          : t('contracts.preview.paymentFinal')}
                    </span>
                    {milestone.endDate && (
                      <span className="d-block small text-muted">
                        {t('contracts.wizard.review.estimatedEnd', { date: milestone.endDate })}
                      </span>
                    )}
                  </span>
                  <span className="contract-preview-milestone-amount">
                    {hasRot
                      ? t('contracts.wizard.review.afterRotAmount', { amount: formatKronor(milestone.afterRotAmount) })
                      : `${formatKronor(milestone.afterRotAmount)} ${kr}`}
                  </span>
                </li>
                );
              })}
            </ol>
          </>
        )}
        <p className="text-muted small mt-2 mb-0" data-testid="contract-preview-payment-terms">{t(paymentTermsKey)}</p>
      </section>

      <section className="contract-preview-section">
        {sectionHeading(t('contracts.preview.specialTermsTitle'), editButton(onEditAssignment, t('contracts.wizard.review.editAttachmentsAria')))}
        <p className="text-muted fst-italic small">{t('contracts.preview.specialTermsNote')}</p>
        {onOpenAttachment && onVerifyAttachment ? (
          <ContractAttachmentList attachments={attachments} onOpen={onOpenAttachment} onVerify={onVerifyAttachment} />
        ) : attachments.length === 0 ? (
          <p className="text-muted mb-0">{t('contracts.preview.noAttachments')}</p>
        ) : (
          <ul className="list-unstyled mb-0 d-flex flex-column gap-1">
            {attachments.map((doc, index) => (
              <li key={doc.id} className="d-flex align-items-center gap-2">
                <i className="feather-paperclip" aria-hidden="true"></i>
                <span className="text-break">
                  {t('contracts.wizard.review.attachmentLine', { number: index + 1, name: doc.originalName || doc.fileName })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="contract-preview-section">
        {sectionHeading(t('contracts.preview.signaturesTitle'))}
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <p className="contract-preview-party-role">{t('contracts.preview.company')}</p>
            <p className="mb-0">{companyName || '—'}</p>
            <p className="mb-0 small text-muted">{signatureLine(signedAt?.company)}</p>
          </div>
          <div className="col-12 col-md-6">
            <p className="contract-preview-party-role">{t('contracts.preview.orderer')}</p>
            {customerName ? <p className="mb-0">{customerName}</p> : customerPlaceholder}
            <p className="mb-0 small text-muted">{signatureLine(signedAt?.customer)}</p>
          </div>
        </div>
      </section>
    </article>
  );
}

export default ContractDocumentPreview;
